/**
 * ContentHistoryTab — 문제 변경 이력 보기·되돌리기 (/api/v2/problem-content).
 *
 * 2026-08-30(계약 `BE-20260830-reply-ocr-repromote-history-close.md` §2)부터 **본문만이 아니다** —
 * 본문·풀이·정답·라벨·개념·난이도 여섯이 한 줄에 쌓인다. 규칙은 revisionFields.js 에 있다.
 *
 * 되돌리기 = 과거 판을 현재로 복사(새 revision append) — 별도 취소가 없고, 잘못 되돌렸으면
 * 다시 되돌리면 된다. head_is_live=false 는 이력 밖 경로로 본문이 수정된 상태라는 뜻이라
 * 최신 판 미리보기가 현재 본문과 다를 수 있음을 배너로 알린다.
 *
 * ── 이 화면이 지키는 것 넷 ──
 * ① **되돌리기는 그 판의 필드만 바꾼다.** 정답 판을 되돌리면 정답만 바뀐다(계약 §2).
 *    종전 확인창은 무엇을 고르든 「현재 **본문**으로 되돌립니다」라고 말했다 — 거짓이었다.
 * ② **[최신]과 잠금은 필드별이다.** 판 번호는 문제 전체에서 하나씩 오르므로 목록 첫 줄이
 *    곧 그 필드의 최신은 아니다. 서버가 준 `heads` 를 쓴다.
 * ③ **미리보기 렌더러가 필드로 갈린다.** 본문·풀이만 Lexical 문서다. 정답을 편집기에 넣으면
 *    normalizeEditorState 가 JSON 파싱에 실패해 **빈 상자**가 뜬다(종전 상태가 그랬다).
 * ④ **번호줄이 둘이라는 것을 화면이 먼저 말한다.** 문제 「rev 17」과 그림 「v4」는 다른 줄이다.
 *    묻기 전에 말해 두지 않으면 「그림 v4 를 되돌리려면 rev 몇 번을 눌러야 하나」가 된다.
 *    표기를 「17판」에서 「rev 17」로 바꾼 것이 오너 지시(2026-08-30)이고, 그림 쪽 「v4」는 그대로 둔다.
 * ⑤ **밖에서 바뀌면 다시 읽는다.** 이 패널은 탭을 옮겨도 언마운트되지 않아(ProblemHub 가 hidden
 *    으로만 감춘다) 신호가 없으면 마운트 때의 목록을 영원히 들고 있다. reloadKey 가 그 신호다.
 *
 * 판 미리보기는 OcrLexicalEditor(readOnly) 재사용 — content 가 이미지 복원된 Lexical
 * (문자열/객체 모두 normalizeEditorState 가 흡수)이라 문제은행 편집기와 같은 모양으로 보인다.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import MathName from '../SyllabusV2/MathName';
import { History, RotateCcw, RefreshCw } from 'lucide-react';
import { InlineMath } from 'react-katex';
import { Button } from '../common';
import { useAuth } from '../../context/AuthContext';
import { formatV2Timestamp } from '../../utils/v2DateTime';
import OcrLexicalEditor from '../OcrHub/OcrLexicalEditor';
import parseAnswerForDisplay from '../OcrHub/answerLatex';
import { fetchContentRevisions, fetchContentRevision, revertContent, getContentSourceText, AUTH_REDIRECT } from './analysisApi';
import {
  revisionFieldKey, revisionFieldName, isDocRevision, isClearedDoc,
  revisionHeads, presentRevisionFields, describeRevisionValue, isFieldAware, revisionOptionLabel,
  revisionStateAt, REVISION_PREVIEW_FIELDS, revisionFieldNameByKey,
} from './revisionFields';

const fmt = (iso) => formatV2Timestamp(iso, 'dateTimeDot') || '-';

/** 작은 필드 한 판의 값. 정답은 수식일 수 있어 본문·정답칸과 같은 규칙으로 그린다. */
function ValueView({ field, text }) {
  if (text == null) {
    return <span className="text-sm text-gray-400">이 버전의 값이 응답에 실려 오지 않았습니다.</span>;
  }
  if (field !== 'answer') return <span className="text-sm text-gray-800"><MathName text={text} compact /></span>;   // 개념 이름 판의 $…$ 도 수식으로
  const shown = parseAnswerForDisplay(text);
  if (shown.mode === 'empty') return <span className="text-sm text-gray-400">정답 없음</span>;
  if (shown.mode === 'text') return <span className="text-sm font-medium text-gray-900">{shown.value}</span>;
  return (
    <span className="text-sm font-medium text-gray-900">
      <InlineMath
        math={shown.value}
        renderError={() => <code className="rounded bg-gray-100 px-1 text-[0.9em] text-gray-700">{text}</code>}
      />
    </span>
  );
}

/**
 * @param canRevert [되돌리기] 를 세울지 — 서버가 교사에게 403 으로 막은 한 경로다
 *   (`POST /problem-content/problems/{id}/revert`, BE-20260910 회신 §1-2).
 *
 * ⚠ 이 저장소의 「되돌리기」는 셋인데 막힌 것은 **문제 본문** 이것 하나뿐이다 —
 * 시험지 되돌리기(`POST /papers/{id}/revert`)와 그림 되돌리기는 교사에게 열려 있다(§1-1).
 * 이름이 같다고 함께 감추지 마라.
 *
 * 목록·판 비교·필터·새로고침은 전부 GET 이라 그대로 둔다 — 교사도 이력을 읽는다.
 * 기본값이 허용(true)인 이유는 형제 카드와 같다: prop 을 빠뜨린 마운트 지점에서 버튼이 말없이 사라지지 않게.
 */
function ContentHistoryTab({ problemId, reloadKey = 0, onChanged = null, canRevert = true }) {
  const { getAccessToken } = useAuth();
  const [list, setList] = useState(null); // { items, heads, head_is_live }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRev, setSelectedRev] = useState(null);
  /**
   * 판 내용 캐시 `{ [revision]: { loading, content, error } }`.
   *
   * 한 화면이 **판을 둘 이상** 받는다 — 「rev 21 시점의 문제」를 그리려면 그때의 본문 판과 풀이 판을
   * 각각 받아야 하고, 그 둘은 rev 21 이 아니다(계약 §2 — 번호가 필드 무관하게 오른다).
   * 판 내용은 append-only 라 **불변**이므로 한 번 받으면 다시 받지 않는다.
   * 정답·라벨·개념·난이도는 목록에 `value` 가 실려 오므로 이 캐시를 쓰지 않는다.
   */
  const [contents, setContents] = useState({});
  const askedRef = useRef(new Set());
  const [reverting, setReverting] = useState(false);
  const [notice, setNotice] = useState(null);
  // 좁혀 보기. null = 전체. 계약의 `?field=` 대신 화면에서 거른다(analysisApi 주석).
  const [fieldFilter, setFieldFilter] = useState(null);
  const problemRef = useRef(problemId);
  useEffect(() => { problemRef.current = problemId; }, [problemId]);

  const load = useCallback(async () => {
    if (problemId == null) return;
    const target = problemId;
    setLoading(true);
    setError(null);
    try {
      const d = await fetchContentRevisions(target, getAccessToken);
      if (String(target) !== String(problemRef.current)) return;
      // 계약이 정렬 순서를 명시하지 않는다 — revision 은 append 단조 증가이므로 내림차순으로
      // 스스로 정렬해 '첫 항목 = 최신 판' 가정을 서버 순서에서 독립시킨다.
      const sorted = {
        ...d,
        items: [...(Array.isArray(d?.items) ? d.items : [])].sort((a, b) => (b.revision ?? 0) - (a.revision ?? 0)),
      };
      // 판 내용은 불변이지만 여기서 통째로 비운다 — 실패한 판만 골라 버리려면 상태와 ref 를
      // 두 곳에서 맞춰야 하고, 그 복잡함이 GET 두 번보다 비싸다. [새로 읽기]가 곧 재시도가 된다.
      setContents({});
      askedRef.current = new Set();
      setList(sorted);
      // 최신 판을 기본 선택 — 목록만 있고 본문이 안 보이면 "이력"이 아니라 표다
      const first = sorted.items.length > 0 ? sorted.items[0].revision : null;
      setSelectedRev(first);
    } catch (e) {
      if (String(target) !== String(problemRef.current)) return;
      setError(e.message || '문제 이력을 불러오지 못했습니다.');
      setList(null);
    } finally {
      if (String(target) === String(problemRef.current)) setLoading(false);
    }
  }, [problemId, getAccessToken]);

  useEffect(() => {
    setList(null);
    setSelectedRev(null);
    setFieldFilter(null);
    setContents({});
    askedRef.current = new Set();
    setNotice(null);
    load();
  }, [load]);

  /**
   * 밖에서 값이 바뀌면 다시 읽는다(§⑤).
   *
   * 이 패널은 탭을 옮겨도 **언마운트되지 않는다** — ProblemHub 가 방문한 탭을 전부 마운트한 채
   * hidden 으로만 감춘다. 그래서 신호가 없으면 마운트 때 받은 목록을 영원히 들고 있고,
   * 저장해도 새 rev 가 나타나지 않는다(오너 보고 2026-08-30 「방금 풀이를 수정했는데 안 보임」).
   *
   * 되돌리기가 낸 신호는 건너뛴다 — 그 경로는 이미 스스로 load() 한 뒤 onChanged 를 부르므로,
   * 안 거르면 되돌릴 때마다 같은 목록을 두 번 읽는다. 조회 탭이 쓰는 방식과 같다
   * (ProblemViewTab.prevRefreshKeyRef).
   */
  const prevReloadRef = useRef(reloadKey);
  const selfBumpRef = useRef(false);
  useEffect(() => {
    if (prevReloadRef.current === reloadKey) return;
    prevReloadRef.current = reloadKey;
    if (selfBumpRef.current) { selfBumpRef.current = false; return; }
    load();
  }, [reloadKey, load]);

  const items = useMemo(() => (Array.isArray(list?.items) ? list.items : []), [list]);
  const heads = useMemo(() => revisionHeads(list), [list]);
  const fields = useMemo(() => presentRevisionFields(items), [items]);
  // 서버가 필드를 실어 주는가 — 「아직 정답 rev 가 없다」와 「서버가 아직 안 준다」를 가른다
  const fieldAware = useMemo(() => isFieldAware(items), [items]);
  const shown = useMemo(
    () => (fieldFilter == null ? items : items.filter((it) => revisionFieldKey(it) === fieldFilter)),
    [items, fieldFilter],
  );

  const selected = useMemo(
    () => items.find((it) => Number(it.revision) === Number(selectedRev)) || null,
    [items, selectedRev],
  );
  const selField = selected ? revisionFieldKey(selected) : 'body';
  const selName = selected ? revisionFieldName(selected) : '본문';
  const selIsHead = selected != null && Number(heads[selField]) === Number(selectedRev);

  /**
   * **그 시점의 문제** — 칸마다 「rev N 이하에서 가장 큰 그 필드 판」을 찾는다.
   *
   * 오너 지시 2026-08-30: 「드롭다운으로 바꿨다고 하단 내용을 요약하면 안 되지. 본문·정답·풀이
   * 모두 원래처럼 보여야 함.」 고른 판의 필드 하나만 그리면 그것은 「그때 무엇이 바뀌었나」이지
   * 「그때 문제가 어땠나」가 아니다. 사람이 이력에서 알고 싶은 것은 뒤엣것이다.
   *
   * 고른 판이 라벨·개념·난이도면 그 칸을 하나 더 붙인다 — 안 붙이면 **바뀐 것이 화면에 없다.**
   *
   * 칸마다 `kind` 가 함께 온다(exact·earliest·none). **그 시점 기록이 없는 것을 「없었다」로
   * 단정하지 않기 위해서다** — 필드별 이력은 나중에 시작해서, 그 전 시점에는 풀이가 멀쩡히
   * 있었는데도 판이 없다(오너 보고 2026-08-30).
   */
  const sections = useMemo(() => {
    if (selectedRev == null) return [];
    const keys = [...REVISION_PREVIEW_FIELDS];
    if (!keys.includes(selField)) keys.push(selField);
    return keys.map((key) => ({ key, ...revisionStateAt(items, key, selectedRev) }));
  }, [items, selectedRev, selField]);

  /**
   * 좁혀 보기를 바꿨는데 고른 rev 가 그 안에 없으면 첫 줄로 옮긴다.
   * 목록일 때는 「고른 줄이 안 보인다」로 끝났지만, 드롭다운은 그 상태를 **빈 칸**으로 그린다 —
   * 무엇을 보고 있는지 화면이 말하지 못하게 된다.
   */
  useEffect(() => {
    if (shown.length === 0) return;
    if (shown.some((it) => Number(it.revision) === Number(selectedRev))) return;
    setSelectedRev(shown[0].revision);
  }, [shown, selectedRev]);

  /**
   * 이 화면이 그리려면 받아야 하는 판들. **본문·풀이만**이다 — 나머지 넷은 목록에 `value` 가
   * 실려 온다(계약 §2). 옛 배포분이 그 키를 안 주는 경우만 예외로 함께 받는다.
   */
  const wanted = useMemo(() => {
    const out = [];
    sections.forEach(({ key, at }) => {
      if (at == null) return;
      if (isDocRevision(key) || at.value === undefined) out.push(Number(at.revision));
    });
    return [...new Set(out)];
  }, [sections]);
  const wantedKey = wanted.join(',');

  useEffect(() => {
    if (problemId == null || wantedKey === '') return undefined;
    let alive = true;
    // 이미 물어본 판은 다시 묻지 않는다 — 내용이 불변이라 다시 받을 이유가 없고,
    // 실패한 판을 무한히 재시도하지 않기 위해서도 이 표시가 필요하다([새로 읽기]가 재시도다).
    wantedKey.split(',').map(Number).forEach((rev) => {
      if (askedRef.current.has(rev)) return;
      askedRef.current.add(rev);
      setContents((c) => ({ ...c, [rev]: { loading: true } }));
      fetchContentRevision(problemId, rev, getAccessToken)
        .then((d) => {
          if (!alive) return;
          setContents((c) => ({ ...c, [rev]: { loading: false, content: d?.content ?? null } }));
        })
        .catch((e) => {
          if (!alive) return;
          setContents((c) => ({ ...c, [rev]: { loading: false, error: e.message || '버전 전문을 불러오지 못했습니다.' } }));
        });
    });
    return () => { alive = false; };
  }, [problemId, wantedKey, getAccessToken]);

  const handleRevert = async () => {
    if (reverting || selectedRev == null || selected == null) return;
    // 무엇이 바뀌는지를 이름으로 말한다 — 「본문으로 되돌립니다」는 정답 판에서 거짓이었다.
    // 비운 판(isClearedDoc)으로 되돌리면 그 칸이 비워진다 — 「되돌린다」만으로는 지워지는 줄 모른다.
    const clearing = isDocRevision(selField) && isClearedDoc(contents[selectedRev]?.content);
    if (!window.confirm(
      `${selName} rev ${selectedRev} 로 되돌립니다 — 이 되돌리기는 ${selName}만 바꿉니다.\n`
      + (clearing ? `⚠ 이 rev 는 비운 판입니다 — 되돌리면 지금 ${selName} 내용이 비워집니다.\n` : '')
      + '기존 이력은 지워지지 않고 새 rev 가 하나 더 쌓입니다(되돌리기도 이력입니다).\n\n되돌릴까요?',
    )) return;
    setReverting(true);
    setNotice(null);
    const prevHead = heads[selField];
    try {
      const d = await revertContent(problemId, selectedRev, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      // 계약 §2: 이미 같은 값이면 200 이고 판이 쌓이지 않는다. 그때 「되돌렸습니다」라고 하면
      // 사람이 이력에서 새 판을 찾다가 없어서 실패로 읽는다.
      const moved = d?.new_head_revision != null && Number(d.new_head_revision) !== Number(prevHead);
      setNotice({
        type: 'info',
        text: moved
          ? `${selName} rev ${selectedRev} 로 되돌렸습니다 — 새 rev 가 쌓여 최신이 되었습니다.`
          : `${selName} — 이미 rev ${selectedRev} 와 같아 바뀐 것이 없습니다.`,
      });
      await load();
      selfBumpRef.current = true; // 방금 읽었다 — 이 신호가 돌아와도 다시 읽지 않는다
      onChanged?.(); // [문제 보기] 탭이 옛 값을 계속 보여 주지 않게
    } catch (e) {
      setNotice({ type: 'error', text: e.message || '되돌리기에 실패했습니다.' });
    } finally {
      setReverting(false);
    }
  };

  if (problemId == null) return null;
  if (loading) return <div className="p-4 text-sm text-gray-400">이력을 읽는 중…</div>;
  if (error) {
    return (
      <div className="space-y-2 p-4">
        <p className="text-sm text-danger">{error}</p>
        <Button size="xs" variant="secondary" onClick={load}>다시 시도</Button>
      </div>
    );
  }
  if (items.length === 0) {
    return <p className="p-4 text-sm text-gray-400">이력이 없습니다 — 이 문제가 수정될 때부터 쌓입니다.</p>;
  }

  return (
    <div className="space-y-3">
      {/* 좁혀 보기 — 없는 필드의 탭은 내지 않는다(눌러도 빈 목록인 버튼을 내지 않는다).
          [새로 읽기]는 **늘 낸다** — reloadKey 가 덮지 못하는 변화(다른 사람의 저장, 다른 창에서
          한 일)가 남고, 그때 「보이는 것을 믿을 수 없다」를 풀 길이 이 버튼뿐이다. */}
      <div className="flex flex-wrap items-center gap-1.5">
        {fields.length > 1 && (
          <div className="flex flex-wrap overflow-hidden rounded-full bg-gray-100 text-[11px] text-gray-500" role="group" aria-label="이력 좁혀 보기">
            <button
              type="button"
              onClick={() => setFieldFilter(null)}
              aria-pressed={fieldFilter == null}
              className={`px-3 py-1 ${fieldFilter == null ? 'bg-primary text-white' : 'hover:bg-gray-200'}`}
            >
              전체
            </button>
            {fields.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFieldFilter(f.key)}
                aria-pressed={fieldFilter === f.key}
                className={`px-3 py-1 ${fieldFilter === f.key ? 'bg-primary text-white' : 'hover:bg-gray-200'}`}
              >
                {f.name}
              </button>
            ))}
          </div>
        )}
        <span className="ml-auto" />
        <Button size="xs" variant="secondary" onClick={load} disabled={reverting} icon={<RefreshCw className="size-3" />}>
          새로 읽기
        </Button>
      </div>

      {/**
        * 필드가 하나뿐이면 위 좁혀 보기가 안 뜬다. 그때 **왜 없는지**를 말해야 한다 —
        * 오너가 실제로 물었다(2026-08-30 「정답 풀이 내역은 없는데?」). 화면에는 두 원인이
        * 똑같이 「본문뿐」으로 보이는데 할 일이 정반대다: 앞엣것은 정답을 한 번 고치면 풀리고,
        * 뒤엣것은 백엔드 배포를 기다려야 한다.
        */}
      {fields.length <= 1 && (
        <p className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-[11px] leading-relaxed text-gray-500">
          {fieldAware
            ? '아직 본문 rev 만 있습니다 — 정답·풀이·라벨·개념·난이도를 고쳐 저장하면 여기에 함께 쌓입니다. 2026-08-30 이전 버전은 전부 본문으로 기록돼 있습니다.'
            : '이 응답에는 필드 구분이 실려 오지 않아 전부 본문으로 봅니다 — 정답·풀이 이력은 서버가 그 값을 주기 시작할 때부터 갈립니다.'}
        </p>
      )}

      {/**
        * 한 문장만 둔다(오너 지시 2026-08-30 「쓸데없는 말이 너무 많다」).
        *
        * 종전에는 네 줄이었다 — 번호가 건너뛰는 이유·그림 이력의 자리·rev 와 v 가 짝이 아니라는 것·
        * 필드별 기록이 나중에 시작했다는 것. 넷 다 **필요할 때 그 자리에서** 이미 말하고 있다:
        * 건너뜀은 드롭다운의 필드 꼬리표가, 이력 시작 전은 칸마다 뜨는 [이력 시작 전] 배지가 말한다.
        * 늘 띄우는 안내가 길면 정작 읽어야 할 때 읽히지 않는다.
        */}
      <p className="text-[11px] leading-relaxed text-gray-400">
        본문·정답·풀이·라벨·개념·난이도의 이력이 모두 쌓입니다.
      </p>

      {/* 이 배너는 계약 §2 가 **본문 기준**이라 못박은 값이다 — 정답·라벨을 보는 중에 띄우면 엉뚱하다 */}
      {list?.head_is_live === false && (fieldFilter == null || fieldFilter === 'body') && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          최신 버전 이후 이력 밖 경로로 본문이 수정되었습니다 — 아래 본문 최신 버전이 현재 본문과 다를 수 있습니다.
        </div>
      )}
      {/* 되돌리기는 되돌릴 수 없다(새 판이 쌓인다) — 결과를 전하는 통로가 이 한 줄뿐이라 항상 마운트한다 */}
      <p
        role={notice?.type === 'error' ? 'alert' : 'status'}
        aria-live={notice?.type === 'error' ? 'assertive' : 'polite'}
        className={`text-xs ${notice ? (notice.type === 'error' ? 'text-danger' : 'text-gray-600') : 'sr-only'}`}
      >
        {notice?.text || ''}
      </p>

      {/**
        * 판 고르기 — **네이티브 `<select>`** 다(오너 지시 2026-08-30 「드롭다운으로 선택하도록」).
        *
        * 왜 직접 만들지 않나: 이 저장소에는 공용 드롭다운이 없고, 새로 만들면 바깥 클릭·ESC·포커스·
        * 겹침을 전부 새로 짜야 한다. z 층은 이미 13개인데 범위가 9999600~9999999 라(z-index.css)
        * 거기에 하나를 더 끼우는 것이 이 화면 하나가 질 값이 아니다. 네이티브는 그 넷을 브라우저가
        * 맡고 키보드도 그냥 되며, 판이 22개에서 100개가 돼도 그대로 돈다.
        * 이 저장소가 이미 같은 선택을 하고 있다 — FigureModal 의 눈금 고르기, FigSpecForm 의 화살표.
        *
        * 배지(필드·최신)는 잃지 않는다 — 고른 뒤 **아래 미리보기 머리줄**로 옮겨 갔다.
        */}
      <div className="flex items-center gap-2">
        <History className="size-4 shrink-0 text-gray-400" />
        <select
          value={selectedRev ?? ''}
          onChange={(e) => setSelectedRev(Number(e.target.value))}
          aria-label="지난 버전 고르기"
          className="min-w-0 flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-primary focus:outline-none"
        >
          {shown.map((it) => (
            <option key={it.revision} value={it.revision}>
              {revisionOptionLabel(it, {
                isHead: Number(heads[revisionFieldKey(it)]) === Number(it.revision),
                sourceText: getContentSourceText(it.source),
                time: fmt(it.created_at),
              })}
            </option>
          ))}
        </select>
        {/* 몇 개 중에 고르는지 — 드롭다운은 접히면 전체 규모가 안 보인다 */}
        <span className="shrink-0 text-[11px] text-gray-400">{shown.length}개</span>
      </div>

      {/**
        * **그 시점의 문제** — 본문·정답·풀이를 함께 세운다(오너 지시 2026-08-30).
        * 되돌리기는 그중 **고른 판의 필드 하나**에만 걸린다 — 칸마다 그것을 표시해 헷갈리지 않게 한다.
        */}
      {selected != null && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
            <span className="text-xs font-medium text-gray-700">rev {selectedRev} 시점의 문제</span>
            {selIsHead && (
              <span className="rounded-full bg-green-50 px-1.5 py-0.5 text-[10px] text-green-700">최신</span>
            )}
            <span className="text-[11px] text-gray-400">
              이 버전에서 바뀐 것은 <b className="text-gray-600">{selName}</b> · {getContentSourceText(selected.source)} · {fmt(selected.created_at)}
            </span>
            <span className="ml-auto" />
            {canRevert && (
              <Button
                size="xs"
                variant="secondary"
                onClick={handleRevert}
                loading={reverting}
                /* 잠금도 필드별이다 — 목록 첫 줄이 곧 그 필드의 최신은 아니다(§②) */
                disabled={reverting || selIsHead}
                icon={<RotateCcw className="size-3.5" />}
                title={selIsHead
                  ? `이미 ${selName}의 최신 rev 입니다`
                  : `${selName}만 이 rev 로 되돌립니다(새 rev 가 쌓입니다)`}
              >
                {selName} 되돌리기
              </Button>
            )}
          </div>

          {sections.map(({ key, at, kind }) => {
            const name = at ? revisionFieldName(at) : revisionFieldNameByKey(key);
            const changed = kind === 'exact' && key === selField;
            const box = at == null ? null : contents[Number(at.revision)];
            return (
              <div key={key}>
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-gray-500">{name}</span>
                  {/* 이 rev 에서 바뀐 칸과 「그때도 그대로였던」 칸을 반드시 가른다 —
                      안 가르면 세 칸이 전부 이 판의 것으로 읽히고, 되돌리기 범위를 오해한다.
                      셋째 갈래(earliest)가 2026-08-30 수정분이다: 그 시점 기록이 없다고 해서
                      **없었다고 말하지 않는다.** 필드별 이력이 나중에 시작했을 뿐이다. */}
                  {changed ? (
                    <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                      이 rev 에서 바뀜
                    </span>
                  ) : kind === 'exact' ? (
                    <span className="text-[10px] text-gray-400">rev {at.revision} 이후 그대로</span>
                  ) : kind === 'earliest' ? (
                    <>
                      <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">이력 시작 전</span>
                      <span className="text-[10px] text-gray-400">
                        {name} 기록은 rev {at.revision} 부터입니다 — 가장 이른 버전을 대신 보이며, 그때 값과 다를 수 있습니다
                      </span>
                    </>
                  ) : (
                    <span className="text-[10px] text-gray-400">이 문제에는 {name} 이력이 없습니다 — 값이 없다는 뜻은 아닙니다</span>
                  )}
                </div>
                {at == null ? (
                  <div className="rounded-xl border border-gray-100 px-3 py-2.5 text-sm text-gray-400">—</div>
                ) : isDocRevision(key) ? (
                  box?.loading ? (
                    <div className="rounded-xl border border-gray-100 p-4 text-sm text-gray-400">버전 전문을 읽는 중…</div>
                  ) : box?.error ? (
                    <div className="rounded-xl border border-red-100 bg-red-50 p-3 text-sm text-red-700">{box.error}</div>
                  ) : isClearedDoc(box?.content) ? (
                    /* 비운 판 — 편집기가 못 여는 모양이라 넘기면 「형식이 올바르지 않습니다」가 떴다(고장이 아니다) */
                    <div className="rounded-xl border border-gray-100 px-3 py-2.5 text-sm text-gray-500">
                      {name} 비움<span className="text-gray-400"> — 이 시점에는 {name} 내용이 없습니다</span>
                    </div>
                  ) : (
                    /* 칸마다 따로 스크롤한다 — 하나로 묶어 자르면 아래 칸에 닿으려고 매번 긁어야 한다 */
                    <div className="max-h-[40vh] overflow-y-auto rounded-xl border border-gray-200">
                      <OcrLexicalEditor
                        key={`${problemId}:${key}:${at.revision}`}
                        initialState={box?.content ?? null}
                        label={name}
                        readOnly
                      />
                    </div>
                  )
                ) : (
                  <div className="rounded-xl border border-gray-200 px-3 py-2.5">
                    <ValueView
                      field={key}
                      text={at.value !== undefined
                        ? describeRevisionValue(key, at.value)
                        : describeRevisionValue(key, box?.content)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ContentHistoryTab;
